import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Camera, Search, MapPin, Star, Filter, ArrowLeft, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { auth, photographers as photographersApi } from '../lib/api';
import { formatINR, isAuthenticated, dashboardPathForRole, getStoredUser, clearSession } from '../lib/auth';

const SPECIALTIES = ['wedding', 'food', 'fashion', 'corporate', 'portrait', 'event', 'product'];

const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1606800052052-a08af7148866?w=800&h=600&fit=crop';
const PLACEHOLDER_AVATAR = 'https://images.unsplash.com/photo-1519741497674-611481863552?w=200&h=200&fit=crop';

const PhotographerListing = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get('category');
  const initialSearch = searchParams.get('search') || '';

  const [search, setSearch] = React.useState(initialSearch);
  const [specialty, setSpecialty] = React.useState(category || '');
  const [photographers, setPhotographers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [meta, setMeta] = React.useState(null);

  const user = getStoredUser();

  const handleLogout = async () => {
    try {
      await auth.logout();
    } catch {
      // ignore - we're clearing the local session regardless
    }
    clearSession();
    window.location.href = '/auth';
  };

  const fetchPhotographers = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (specialty) params.specialty = specialty;
      const { data } = await photographersApi.list(params);
      setPhotographers(data.data || []);
      setMeta(data);
    } catch (err) {
      toast.error('Could not load photographers right now.');
    } finally {
      setLoading(false);
    }
  }, [search, specialty]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPhotographers();
  };

  const toggleSpecialty = (value) => {
    const next = specialty === value ? '' : value;
    setSpecialty(next);
    setSearchParams(next ? { category: next } : {});
  };

  React.useEffect(() => {
    fetchPhotographers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialty]);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-background sticky top-0 z-50">
        <div className="px-6 md:px-12 lg:px-24 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
              <Camera className="w-8 h-8" />
              <span className="font-playfair text-2xl font-bold">Pixnest</span>
            </Link>
            <div className="flex items-center space-x-4">
              {isAuthenticated() ? (
                <>
                  <Link to={dashboardPathForRole(user?.role)}>
                    <Button size="sm" data-testid="header-dashboard">Dashboard</Button>
                  </Link>
                  <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="header-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Log Out
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/auth">
                    <Button variant="ghost" size="sm" data-testid="header-signin">Sign In</Button>
                  </Link>
                  <Link to="/auth">
                    <Button size="sm" data-testid="header-dashboard">Get Started</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="px-6 md:px-12 lg:px-24 py-12">
        {/* Back Button */}
        <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8" data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        {/* Search and Filters */}
        <div className="mb-12">
          <h1 className="font-playfair text-4xl md:text-5xl font-bold mb-6" data-testid="page-title">
            {specialty ? `${specialty.charAt(0).toUpperCase() + specialty.slice(1)} Photographers` : 'Find Your Perfect Photographer'}
          </h1>

          <form className="flex flex-col md:flex-row gap-4 mb-6" onSubmit={handleSearch}>
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search by name, specialty, or city (e.g. Kolkata)..."
                className="pl-12"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="search-input"
              />
            </div>
            <Button type="submit" variant="outline" data-testid="filter-button">
              <Filter className="w-4 h-4 mr-2" />
              Search
            </Button>
          </form>

          {/* Quick Filters */}
          <div className="flex flex-wrap gap-2" data-testid="quick-filters">
            {SPECIALTIES.map((s) => (
              <Badge
                key={s}
                variant={specialty === s ? 'default' : 'outline'}
                className="cursor-pointer hover:bg-accent capitalize"
                onClick={() => toggleSpecialty(s)}
                data-testid={`filter-${s}`}
              >
                {s}
              </Badge>
            ))}
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6 text-muted-foreground" data-testid="results-count">
          {loading ? 'Loading photographers…' : `Showing ${photographers.length} of ${meta?.total ?? photographers.length} photographers`}
        </div>

        {!loading && photographers.length === 0 && (
          <div className="text-center py-24 text-muted-foreground" data-testid="empty-state">
            No photographers match your search yet. Try a different city or specialty.
          </div>
        )}

        {/* Photographer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="photographer-grid">
          {photographers.map((photographer) => {
            const locationParts = [photographer.city, photographer.state].filter(Boolean);
            return (
              <Card key={photographer.id} className="overflow-hidden hover:shadow-xl transition-shadow duration-300" data-testid={`photographer-card-${photographer.id}`}>
                <div className="grid md:grid-cols-5 gap-0">
                  {/* Portfolio Image */}
                  <div className="md:col-span-2 relative h-64 md:h-auto">
                    <img
                      src={photographer.cover_photo_path || PLACEHOLDER_IMAGE}
                      alt={photographer.business_name}
                      className="w-full h-full object-cover"
                    />
                    {photographer.specialties?.[0] && (
                      <div className="absolute top-4 left-4">
                        <Badge className="bg-white text-foreground capitalize" data-testid={`photographer-specialty-${photographer.id}`}>
                          {photographer.specialties[0]}
                        </Badge>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <CardContent className="md:col-span-3 p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <img
                          src={photographer.user?.avatar_path || PLACEHOLDER_AVATAR}
                          alt={photographer.business_name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                        <div>
                          <h3 className="font-playfair text-xl font-semibold" data-testid={`photographer-name-${photographer.id}`}>
                            {photographer.business_name || photographer.user?.name}
                          </h3>
                          <div className="flex items-center text-sm text-muted-foreground">
                            <MapPin className="w-3 h-3 mr-1" />
                            <span data-testid={`photographer-location-${photographer.id}`}>
                              {locationParts.length ? locationParts.join(', ') : 'Location not set'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center mb-4">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400 mr-1" />
                      <span className="font-semibold mr-1" data-testid={`photographer-rating-${photographer.id}`}>
                        {Number(photographer.avg_rating || 0).toFixed(1)}
                      </span>
                      <span className="text-sm text-muted-foreground" data-testid={`photographer-reviews-${photographer.id}`}>
                        ({photographer.review_count || 0} reviews)
                      </span>
                    </div>

                    <div className="flex items-center justify-between mt-6">
                      <div>
                        <div className="text-sm text-muted-foreground">Starting at</div>
                        <div className="font-playfair text-2xl font-bold" data-testid={`photographer-price-${photographer.id}`}>
                          {photographer.starting_price ? formatINR(photographer.starting_price) : 'Contact for pricing'}
                        </div>
                      </div>
                      <Link to={`/photographer/${photographer.id}`}>
                        <Button data-testid={`view-profile-button-${photographer.id}`}>View Profile</Button>
                      </Link>
                    </div>
                  </CardContent>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PhotographerListing;
